import { BUFFER_SPICE, CPHY_EYE_MASK, IBIS, SPICE } from "../../../constants/libraryConstants";
import { SERDES_TYPES } from "../../PCBHelper";
import { filterTestPoint } from "../channel";
import { getFileContent } from "../library";
import libraryConstructor from "../library/libraryConstructor";
import { getCtleNodePairList } from "./displayHelper";
import { HspiceSignalConfig, handleComponents } from "./IntegratedHspiceConfig";


function getModelKey({ files, pairIndex, modelKey, spiceType }) {
  let _modelKey = modelKey;
  if (!_modelKey) {
    _modelKey = spiceType === 'only' ? (files[pairIndex % 3] || {}).modelKey : (files[0] || {}).modelKey;
  }
  return _modelKey;
}

// clear subckt
function handleSelectSubckt(modelType, model, subckt, modelKey, file) {
  const _files = [...model.files], _pairs = [...model.pairs];
  const fileIndex = _files.findIndex(item => item.modelKey === modelKey) || 0;
  if (file.subckt !== subckt.name) {
    _pairs.forEach((pair) => {
      if ((modelType === 'txModel' || pair.modelKey === modelKey)
        && pair.subckt === file.subckt && pair.libraryId === file.libraryId && pair.fileName === file.fileName) {
        pair.node = "";
        delete pair.fileName;
        delete pair.libraryId;
        delete pair.subckt;
        delete pair.modelKey;
      }
    })
    _files[fileIndex] = file
  }

  _files[fileIndex] = { ..._files[fileIndex], subckt: subckt.name }
  return { files: _files, pairs: _pairs }
}

function getInorOutPorts(ports) {
  // in / out
  let inPorts = [], outPorts = [], otherPorts = [];
  for (const port of ports) {
    if (/in/i.test(port)) {
      inPorts.push(port)
    } else if (/out/i.test(port)) {
      outPorts.push(port)
    } else {
      otherPorts.push(port)
    }
  }
  return { inPorts, outPorts, otherPorts }
}

// ctleModel auto match node // byName
function autoMatchNode({
  signal,
  model = {},
  subcktList,
  spiceType,
  autoMatchType = "byName",
  isSwap
}) {
  const { files, pairs } = model;
  if (!files.length) return pairs;
  const pairList = getCtleNodePairList({
    pairs,
    signalInfo: signal
  });

  if (spiceType === "only") {
    pairList.forEach((pair, index) => {
      const file = files[index] || {};
      const _modelKey = getModelKey({ spiceType, files, pairIndex: pair.left.pairIndex, modelKey: pair.left.modelKey });
      const ports = (subcktList[_modelKey] || []).ports;
      if (ports && ports.length) {
        let inPorts = [], outPorts = [], startIndex = -1, endIndex = -1;
        if (autoMatchType === 'byName') {
          const namePorts = getInorOutPorts(ports);
          inPorts = namePorts.inPorts;
          outPorts = namePorts.outPorts;
        } else {
          const indexInfo = getPortIndex(autoMatchType, ports.length, isSwap);
          startIndex = indexInfo.startIndex;
          endIndex = indexInfo.endIndex;
        }

        const leftNode = autoMatchType === 'byName' ? inPorts[0] : ports[startIndex];

        pair.left = {
          ...pair.left,
          node: leftNode || "",
          fileName: file.fileName,
          libraryId: file.libraryId,
          modelKey: file.modelKey,
          subckt: file.subckt
        }
        const rightNode = autoMatchType === 'byName' ? outPorts[0] : ports[endIndex];
        pair.right = {
          ...pair.right,
          node: rightNode || "",
          fileName: file.fileName,
          libraryId: file.libraryId,
          modelKey: file.modelKey,
          subckt: file.subckt
        }
      }
    })
  } else {
    const file = files[0] || {};
    const _modelKey = getModelKey({ spiceType, files });
    const ports = (subcktList[_modelKey] || []).ports;
    if (!ports || !ports.length) {
      return pairs;
    }
    // auto match node
    let inPorts = [], outPorts = [], startIndex = -1, endIndex = -1, startAdd = 0, endAdd = 0;
    if (autoMatchType === 'byName') {
      const namePorts = getInorOutPorts(ports);
      inPorts = namePorts.inPorts;
      outPorts = namePorts.outPorts;
    } else {
      const indexInfo = getPortIndex(autoMatchType, ports.length, isSwap);
      startIndex = indexInfo.startIndex;
      endIndex = indexInfo.endIndex;
      startAdd = indexInfo.startAdd;
      endAdd = indexInfo.endAdd;
    }

    let portNumber = 0;
    pairList.forEach((pair, index) => {
      const leftNode = autoMatchType === 'byName' ? inPorts[index] : portNumber < ports.length ? ports[startIndex] : "";
      pair.left = {
        ...pair.left,
        node: leftNode || "",
        fileName: file.fileName,
        libraryId: file.libraryId,
        modelKey: file.modelKey,
        subckt: file.subckt
      }
      if (ports[startIndex]) portNumber++;
      const rightNode = autoMatchType === 'byName' ? outPorts[index] : portNumber < ports.length ? ports[endIndex] : "";
      pair.right = {
        ...pair.right,
        node: rightNode || "",
        fileName: file.fileName,
        libraryId: file.libraryId,
        modelKey: file.modelKey,
        subckt: file.subckt
      }
      if (ports[endIndex]) portNumber++;

      endIndex += endAdd;
      startIndex += startAdd;
    })
  }

  const leftPairs = pairList.map(pair => {
    delete pair.left.pairIndex;
    return pair.left;
  });
  const rightPairs = pairList.map(pair => {
    delete pair.right.pairIndex;
    return pair.right;
  });
  return [...leftPairs, ...rightPairs];
}

function getPortIndex(type, portLength, isSwap) {
  switch (type) {
    case 0:
      return {
        startIndex: isSwap ? 1 : 0,
        endIndex: isSwap ? 0 : 1,
        startAdd: 2,
        endAdd: 2
      }
    case 1:
      return {
        startIndex: isSwap ? Math.ceil(portLength / 2) : 0,
        endIndex: isSwap ? 0 : Math.ceil(portLength / 2),
        startAdd: 1,
        endAdd: 1
      }
    case 2:
      const defaultEndIndex = portLength - 1 === 0 ? -1 : portLength - 1;
      return {
        startIndex: isSwap ? defaultEndIndex : 0,
        endIndex: isSwap ? 0 : defaultEndIndex,
        startAdd: isSwap ? -1 : 1,
        endAdd: isSwap ? 1 : -1
      }
    default:
      return null
  }
}

function handleChangeSpiceType(model, type, subcktList = []) {
  let _files = [...model.files], _pairs = [...model.pairs], _subcktList = { ...subcktList };
  const fileEle = _files.length > 0 ? { ..._files[0] } : {}, subcktInfo = { ..._subcktList[0] };
  if (type === 'share') {
    _files = [{ ...fileEle, modelKey: 1 }];
    // After processing, clear the data about the pairs file selection
    _subcktList = { 1: subcktInfo };
  } else {
    _files = [{ ...fileEle, modelKey: 1 }, { ...fileEle, modelKey: 2 }, { ...fileEle, modelKey: 3 }];
    _subcktList = { 1: subcktInfo, 2: subcktInfo, 3: subcktInfo };
  }
  _pairs.forEach(pair => {
    pair.node = "";
    delete pair.fileName;
    delete pair.libraryId;
    delete pair.subckt;
    delete pair.modelKey;
  })
  return { files: _files, pairs: _pairs, subcktList: _subcktList };
}

function handleSelectFile(modelType, model, file, fileIndex) {
  const _files = [...model.files], _pairs = [...model.pairs];
  // add first file
  if (fileIndex === -1) {
    _files.push({ ...file, modelKey: 1 })
  } else {
    const oldFile = model.files[fileIndex];
    // Select the old files within the processing pairs
    _pairs.forEach((pair, index) => {
      if (modelType === 'txModel' || pair.modelKey === oldFile.modelKey) {
        pair.node = "";
        delete pair.fileName;
        delete pair.libraryId;
        delete pair.subckt;
        delete pair.modelKey;
      }
    })
    _files[fileIndex] = file;
    if (file) {
      _files[fileIndex].modelKey = fileIndex + 1;
    }
  }

  return { files: [..._files], pairs: [..._pairs] }
}

function handleSelectNode(model, spiceType, node, pairIndex, modelKey) {
  const { files = [] } = model;
  const _modelKey = getModelKey({ files, pairIndex, modelKey, spiceType })
  const pairs = [...model.pairs], file = spiceType === 'only' ? files.find(file => file.modelKey === _modelKey) : files[0];
  pairs[pairIndex] = {
    ...pairs[pairIndex],
    node: node,
    fileName: file.fileName,
    libraryId: file.libraryId,
    subckt: file.subckt,
    modelKey: file.modelKey
  }
  return pairs;
}

// The data were postprocessed after modifying the signalName
function updateSignalNameInHspiceConfig(hspiceConfig, prevName, name) {
  if (!hspiceConfig || !hspiceConfig.signals) {
    return hspiceConfig;
  }

  for (let sigItem of hspiceConfig.signals) {
    if (sigItem.signalName === prevName) {
      sigItem.signalName = name;
    }
  }

  return hspiceConfig;
}

// Data were post-processed after signal
function delSignalInHspiceConfig(hspiceConfig, signal, deletedSignals) {
  if (!hspiceConfig || !hspiceConfig.signals) {
    return hspiceConfig;
  }
  hspiceConfig.signals = hspiceConfig.signals.filter(item => item.signalName !== signal && !deletedSignals.includes(item.signalName));

  return hspiceConfig;
}

// If the currently used component is deleted, the controller and device are removed
function updateHspiceConfigComp({ hspiceConfig, components }) {
  if (!hspiceConfig || !hspiceConfig.signals) {
    return hspiceConfig;
  }

  const compNames = components.map(item => item.name);
  if (hspiceConfig.controller && !compNames.includes(hspiceConfig.controller)) {
    hspiceConfig.controller = "";
  }
  if (hspiceConfig.device && !compNames.includes(hspiceConfig.device)) {
    hspiceConfig.device = "";
  }
  return hspiceConfig;
}

// select Net
function updateHSPICEModel(channelInfo, signalName) {
  let hspiceConfig = channelInfo.hspiceConfig ? { ...channelInfo.hspiceConfig } : {};
  const signals = channelInfo.content.signals || [];
  if (!Object.keys(hspiceConfig).length) {
    return hspiceConfig;
  }
  const components = channelInfo.content.components || [];
  const filterComps = filterTestPoint(components, channelInfo.content.comp_merges);
  const ICComps = filterComps.filter(item => SERDES_TYPES.includes(item.type));

  // If there is no component available, empty data within the signals
  if (!ICComps.length) {
    hspiceConfig.controller = "";
    hspiceConfig.device = "";

    hspiceConfig.signals = hspiceConfig.signals.map(item => {
      return new HspiceSignalConfig({
        ...item,
        txModel: {},
        rxModel: {},
        rxCpadModel: {},
        termination: {},
        ctleModel: {}
      });
    })
    return hspiceConfig;
  }

  let controller = hspiceConfig.controller, device = hspiceConfig.device, hspiceSignals = [...hspiceConfig.signals];
  const compNames = ICComps.map(item => item.name);
  if (!hspiceConfig.controller || !compNames.includes(hspiceConfig.controller)) {
    const _icComps = compNames.filter(item => item !== device);
    controller = _icComps.length ? _icComps[0] : "";
  }

  if (!hspiceConfig.device || !compNames.includes(hspiceConfig.device)) {
    const _icComps = compNames.filter(item => item !== controller);
    device = _icComps.length ? _icComps[0] : "";
  }
  // Check the modified signal and get the relationship between comp, pin, net
  const signal = signals.find(item => item.name === signalName);
  const hspiceSignalIndex = hspiceSignals.findIndex(item => item.signalName === signalName);
  if (hspiceSignalIndex > -1) {
    const { controllerPins, devicePins } = handleComponents(ICComps, signal, controller, device);
    const _item = new HspiceSignalConfig(hspiceSignals[hspiceSignalIndex]);
    _item.checkModels(controllerPins, devicePins, signal);
    hspiceSignals[hspiceSignalIndex] = _item;
  }

  return {
    ...hspiceConfig,
    controller,
    device,
    signals: hspiceSignals
  };
}

// select pin
function updateHSPICEPin(hspiceConfig, newPin, prevPin) {
  if (!Object.keys(hspiceConfig).length) {
    return hspiceConfig;
  }
  let hspiceSignals = [...hspiceConfig.signals];
  const hspiceSignalIndex = hspiceConfig.signals.findIndex(item => item.signalName === newPin.signal);
  if (hspiceSignalIndex > -1) {
    const _item = new HspiceSignalConfig(hspiceSignals[hspiceSignalIndex]);
    _item.checkPin(newPin, prevPin);
    hspiceSignals[hspiceSignalIndex] = _item;
  }

  return {
    ...hspiceConfig,
    signals: hspiceSignals
  }
}

// change compType
function updateHSPICECompType(channelInfo) {
  const hspiceConfig = channelInfo.hspiceConfig ? { ...channelInfo.hspiceConfig } : {};
  const signals = channelInfo.content.signals || [];
  if (!Object.keys(hspiceConfig).length) {
    return hspiceConfig;
  }
  const components = channelInfo.content.components || [];
  const ICComps = components.filter(item => SERDES_TYPES.includes(item.type));

  if (!ICComps.length) {
    hspiceConfig.controller = "";
    hspiceConfig.device = "";

    hspiceConfig.signals = hspiceConfig.signals.map(item => {
      return new HspiceSignalConfig({
        ...item,
        txModel: {},
        rxModel: {},
        rxCpadModel: {},
        termination: {},
        ctleModel: {}
      });
    })
    return hspiceConfig;
  }

  let replaceController = false, replaceDevice = false, controller = hspiceConfig.controller, device = hspiceConfig.device, hspiceSignals = [...hspiceConfig.signals];
  const compNames = ICComps.map(item => item.name);
  if (!hspiceConfig.controller || !compNames.includes(hspiceConfig.controller)) {
    const _icComps = compNames.filter(item => item !== hspiceConfig.device);
    controller = _icComps.length ? _icComps[0] : "";
    replaceController = true;
  }

  if (!hspiceConfig.device || !compNames.includes(hspiceConfig.device)) {
    const _icComps = compNames.filter(item => item !== hspiceConfig.controller);
    device = _icComps.length ? _icComps[0] : "";
    replaceDevice = true;
  }

  if (replaceController || replaceDevice) {
    hspiceSignals = hspiceSignals.map(item => {
      let _item = new HspiceSignalConfig(item);
      const signal = signals.find(item => item.name === _item.signalName);
      const { controllerPins, devicePins } = handleComponents(ICComps, signal, controller, device)
      _item = _item.checkComp(controllerPins, devicePins, replaceController, replaceDevice, signal);
      return _item;
    })
  }

  return {
    ...hspiceConfig,
    controller,
    device,
    signals: hspiceSignals
  };
}

async function hspiceConfigErrorCheck(hspiceConfig, selectedSignals, isEndToEnd) {
  let settingErrors = [], errors = [];

  if (!hspiceConfig) {
    settingErrors.push(`Simulation setup is not set.`);
    errors.push({
      title: "[Simulation Settings]",
      monitor: settingErrors
    })
    return errors;
  }

  if (!hspiceConfig.controller || !hspiceConfig.device) {
    settingErrors.push("The component of signals AMI setup are not selected.");
    errors.push({
      title: "[Simulation Settings]",
      monitor: settingErrors
    })
    return errors;
  }

  // check eyeMask
  if (hspiceConfig && hspiceConfig.eyeMask && hspiceConfig.eyeMask.libraryId) {
    const eyeMaskList = libraryConstructor.getLibraryValues(CPHY_EYE_MASK);
    const eyeMask = eyeMaskList.find(item => item.id === hspiceConfig.eyeMask.libraryId);
    if (!eyeMask) {
      settingErrors.push(`The Eye Mask file is not exist.`);
      errors.push({
        title: "[Simulation Settings]",
        monitor: settingErrors
      })
      return errors;
    }
  }

  const ibisLibraryList = libraryConstructor.getLibraryValues(IBIS);
  const bufferSpiceLibraryList = libraryConstructor.getLibraryValues(BUFFER_SPICE);
  const genericSpiceLibraryList = libraryConstructor.getLibraryValues(SPICE);

  // txModel / rxModel / rxCpadModel / termination / ctleModel
  for (const signal of hspiceConfig.signals) {
    if (!selectedSignals.includes(signal.signalName) && !isEndToEnd) {
      continue;
    }
    // txModel
    let signalErrors = [], errorExist = false;
    if (!signal.txModel) {
      errorExist = true;
      signalErrors.push(`The TxModel of ${signal.signalName} is not set.`);
    } else if (signal.txModel.type === SPICE) { // check pairs
      // check file
      const txFileError = await libraryCheck({
        signalName: signal.signalName,
        model: signal.txModel,
        modelType: "TxModel",
        libraryList: [...bufferSpiceLibraryList, ...genericSpiceLibraryList]
      })

      if (txFileError) {
        signalErrors.push(txFileError);
        errorExist = true;
      }

      const txPairsError = pairsCheck({
        signalName: signal.signalName,
        model: signal.txModel,
        modelType: "TxModel"
      })

      if (txPairsError) {
        signalErrors.push(txPairsError);
        errorExist = true;
      }
    } else if (/ibis/.test(signal.txModel.type)) {
      // check file
      const txFileError = await libraryCheck({
        signalName: signal.signalName,
        model: signal.txModel,
        modelType: "TxModel",
        libraryList: ibisLibraryList
      })

      if (txFileError) {
        signalErrors.push(txFileError);
        errorExist = true;
      }

      // check pinModels
      const txPinModelsError = pinModelCheck({
        signalName: signal.signalName,
        model: signal.txModel,
        modelType: "TxModel"
      })

      if (txPinModelsError) {
        signalErrors.push(txPinModelsError);
        errorExist = true;
      }
    }
    // rxModel
    if (signal.rxModel.type !== 'None') {
      if (!signal.rxModel) {
        errorExist = true;
        signalErrors.push(`The RxModel of ${signal.signalName} is not set.`);
      } else if (signal.rxModel.type === SPICE) {
        // check file
        const rxFileError = await libraryCheck({
          signalName: signal.signalName,
          model: signal.rxModel,
          modelType: "RxModel",
          libraryList: [...bufferSpiceLibraryList, ...genericSpiceLibraryList]
        })

        if (rxFileError) {
          signalErrors.push(rxFileError);
          errorExist = true;
        }

        // check pairs
        const rxPairsError = pairsCheck({
          signalName: signal.signalName,
          model: signal.rxModel,
          modelType: "RxModel"
        })

        if (rxPairsError) {
          signalErrors.push(rxPairsError);
          errorExist = true;
        }
      } else if (signal.rxModel.type === IBIS) {
        // check file
        const rxFileError = await libraryCheck({
          signalName: signal.signalName,
          model: signal.rxModel,
          modelType: "RxModel",
          libraryList: ibisLibraryList
        })

        if (rxFileError) {
          signalErrors.push(rxFileError);
          errorExist = true;
        }

        // check pinModels
        const rxPinModelsError = pinModelCheck({
          signalName: signal.signalName,
          model: signal.rxModel,
          modelType: "RxModel"
        })

        if (rxPinModelsError) {
          signalErrors.push(rxPinModelsError);
          errorExist = true;
        }
      }
    }
    // rxCpadModel
    if (signal.rxCpadModel.type !== 'None') {
      if (!signal.rxCpadModel) {
        errorExist = true;
        signalErrors.push(`The RxCpadModel of ${signal.signalName} is not set.`);
      } else if (signal.rxCpadModel.type === SPICE) {
        // check file and pair/pinModel
        const rxCpadFileError = await libraryCheck({
          signalName: signal.signalName,
          model: signal.rxCpadModel,
          modelType: "RxCpadModel",
          libraryList: [...bufferSpiceLibraryList, ...genericSpiceLibraryList]
        })
        if (rxCpadFileError) {
          signalErrors.push(rxCpadFileError);
          errorExist = true;
        }
        // check pairs
        const rxCpadPairsError = pairsCheck({
          signalName: signal.signalName,
          model: signal.rxCpadModel,
          modelType: "RxCpadModel"
        })
        if (rxCpadPairsError) {
          signalErrors.push(rxCpadPairsError);
          errorExist = true;
        }
      }
    }
    // termination
    if (!signal.termination) {
      errorExist = true;
      signalErrors.push(`The termination of ${signal.signalName} is not set.`);
    }
    // ctleModel
    if (signal.ctleModel.type !== 'None') {
      if (!signal.ctleModel) {
        errorExist = true;
        signalErrors.push(`The CTLE model of ${signal.signalName} is not set.`)
      } else {
        // check file
        const ctleFileError = await libraryCheck({
          signalName: signal.signalName,
          model: signal.ctleModel,
          modelType: "CtleModel",
          libraryList: genericSpiceLibraryList
        })
        if (ctleFileError) {
          signalErrors.push(ctleFileError);
          errorExist = true;
        }
        // check pinModel
        const ctlePairsError = pairsCheck({
          signalName: signal.signalName,
          model: signal.ctleModel,
          modelType: "CtleModel"
        })
        if (ctlePairsError) {
          signalErrors.push(ctlePairsError);
          errorExist = true;
        }
      }
    }

    if (errorExist) {
      signalErrors.length && errors.push({
        title: `[Signal - ${signal.signalName}]`,
        monitor: signalErrors
      });
    }
  }
  return errors;
}

async function libraryCheck({ signalName, model, modelType, libraryList = [] }) {
  const files = model.files || [];
  const notExitFile = [];
  for (const file of files) {
    if (file && file.libraryId) {
      const library = libraryList.find(it => it.id === file.libraryId)
      if (!library) {
        if (file.path) {
          const fileInfo = await getFileContent(file.libraryId);
          if (!fileInfo) {
            notExitFile.push(file.fileName);
          }
        } else {
          notExitFile.push(file.fileName);
        }
      }
    }
  }

  if (!notExitFile.length) {
    return null;
  }

  const notExitFileStr = notExitFile.join(", ");
  return `The ${modelType} library ${notExitFileStr} of ${signalName} is not exist.`;
}

function pairsCheck({ signalName, model, modelType }) {
  const pairs = model.pairs || [];
  for (const pair of pairs) {
    if (pair && !pair.node) {
      return `The ${modelType} node of ${signalName} is not set.`;
    }
  }
}

function pinModelCheck({ signalName, model, modelType }) {
  const pinModels = model.pinModels || [];
  for (const pinModel of pinModels) {
    if (pinModel && !pinModel.modelName) {
      return `The ${modelType} model of ${signalName} is not set.`;
    }
  }
}

function checkCtleModelPairs({ hspiceSignals, controller, device, components, signals }) {

  function getCtleModelPairs(ctleModelPairs, controllerPins, devicePins) {
    if (ctleModelPairs && ctleModelPairs.length > 0) {
      return ctleModelPairs.map(pair => {
        const _pair = { ...pair };
        delete _pair.component;
        if (pair.component === device) {
          const contrIndex = devicePins.findIndex(pin => pin.pin === pair.pin);
          const devicePin = devicePins[contrIndex] || {};
          return { ..._pair, pin: (devicePin.pin || "") + "_out", originPin: devicePin.pin || "" };
        } else if (pair.component === controller) {
          const contrIndex = controllerPins.findIndex(pin => pin.pin === pair.pin);
          const devicePin = devicePins[contrIndex] || {};
          return { ..._pair, pin: devicePin.pin || "" };
        }

        return _pair;
      })
    }
  }

  return hspiceSignals.map(signal => {
    const { ctleModel } = signal;
    if (ctleModel && ctleModel.pairs && ctleModel.pairs.length > 0) {
      const signalInfo = signals.find(item => item.name === signal.signalName);
      const { controllerPins, devicePins } = handleComponents(components, signalInfo, controller, device)
      const ctleModelPairs = getCtleModelPairs(ctleModel.pairs, controllerPins, devicePins);
      return { ...signal, ctleModel: { ...ctleModel, pairs: ctleModelPairs } };
    }
    return signal;
  })
}

export {
  handleSelectSubckt,
  handleChangeSpiceType,
  handleSelectFile,
  handleSelectNode,
  updateSignalNameInHspiceConfig,
  delSignalInHspiceConfig,
  updateHspiceConfigComp,
  updateHSPICEModel,
  updateHSPICEPin,
  getModelKey,
  updateHSPICECompType,
  hspiceConfigErrorCheck,
  autoMatchNode,
  checkCtleModelPairs,
  getPortIndex
}